/**
 * AddSocialHistoryEditor — inline Add/Edit form for the Social History
 * section of the History chart tab (PROD-827 chart write surfaces).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/history_tab.dart`
 * `editSocialHistoryView()` at L396-712.
 *
 * **Two modes, same form:**
 * - Add (`editing` omitted) → POST `/CreateSocialHistory` with
 *   `CreateSocialHistoryBody`. `code.category` ships nested under
 *   `code` (the picker row carries the category descriptor); top-level
 *   `category` mirrors it. `createdBy` set to current user;
 *   `performerDetails: null` + `speciality: null` — server fills.
 * - Edit (`editing` is a `PatientSocialHistory`) → PUT
 *   `/socialHistory/{id}` with `UpdateSocialHistoryBody`. Backend strips
 *   `code.category` and surfaces `category` at top-level on round-trip
 *   (we ship `code.category: null` on Edit). `createdBy`, `createdDate`,
 *   `performerDetails`, `speciality` all preserved from the original
 *   record — strict guard refuses to ship if the original `createdBy`
 *   is missing.
 *
 * **Social-History-specific wire quirks** (curl-verified 2026-05-20):
 * - `dataAbsentReason: "n/a"` on BOTH Create and Update (Procedures
 *   differs — Create ships `""`).
 * - `historyCode` is a Coding extended with PascalCase `Definition` +
 *   description + shortName — full row from `GetSocialHabitQtyList`
 *   ships verbatim on the wire.
 * - `fromDisplayTime` / `toDisplayTime` partner the ISO `fromTime` /
 *   `toTime`. The `DynamicDateInput` keeps both in lock-step.
 * - Lowercase `updatedby` wire field (NOT `updatedBy`) — sent as null
 *   on both Create and Update; server stamps it.
 */

import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button, FieldLabel, Select } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useSocialHistoryCodeSearch } from '@/patients/services/use-social-history-code-search';
import { useSocialHabitQtyList } from '@/patients/services/use-social-habit-qty-list';
import { useCreateSocialHistory } from '@/patients/services/use-create-social-history';
import { useUpdateSocialHistory } from '@/patients/services/use-update-social-history';
import {
  readStringField,
  unwrapMasterRow,
} from '@/patients/lib/unwrap-master-row';
import { normalizePerformerDetails } from './add-allergy-dialog';
import { DynamicDateInput } from './dynamic-date-input';
import type {
  CreateSocialHistoryBody,
  PatientSocialHistory,
  SocialHabitQty,
  SocialHistoryCodeRow,
  UpdateSocialHistoryBody,
} from '@/types/patient-chart-api';
import type { CodeWordCase } from '@/types/patient-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';

// ──────────────────────────────────────────────────────────────────────────
// Zod schema
// ──────────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    codeJson: z.string().min(1, 'Pick an observation type'),
    historyCodeKey: z.string().min(1, 'Pick a quantity'),
    fromDisplay: z.string().min(1, 'From date is required').max(50),
    fromIso: z.string().nullable(),
    toDisplay: z.string().max(50).optional(),
    toIso: z.string().nullable().optional(),
    comment: z.string().max(100).optional(),
  })
  .refine((v) => v.fromIso !== null, {
    message:
      'From date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
    path: ['fromDisplay'],
  })
  .refine(
    (v) => {
      if (!v.toDisplay?.trim()) return true;
      return v.toIso != null;
    },
    {
      message:
        'To date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
      path: ['toDisplay'],
    },
  );

type FormValues = z.infer<typeof schema>;

// ──────────────────────────────────────────────────────────────────────────
// Editor
// ──────────────────────────────────────────────────────────────────────────

export interface AddSocialHistoryEditorProps {
  patientId: string;
  onClose: () => void;
  editing?: PatientSocialHistory | null;
}

export function AddSocialHistoryEditor({
  patientId,
  onClose,
  editing = null,
}: AddSocialHistoryEditorProps) {
  const isEditing = !!editing?.identifier;

  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';

  // historyCode picker — small static list, prefetched at mount so the
  // initial form value can resolve the existing code on Edit.
  const habitQ = useSocialHabitQtyList(true);
  // Defensive normalize — `GetSocialHabitQtyList` could surface the
  // wrapped row shape (PR #225/#232/#253 lineage). Map to flat rows
  // ONCE so both the Select render AND the submit-handler `find()`
  // lookup see consistent shapes. Without this, a wrapped row would
  // make `row.code === values.historyCodeKey` always false (the find
  // compares against an object), and the wire body would ship
  // `historyCode: null` even when the user picked one.
  const habitRows = useMemo(() => {
    const raw = habitQ.data ?? [];
    return raw.map((row) => {
      const inner = unwrapMasterRow(row);
      return {
        code: readStringField(inner, 'code'),
        Definition: (inner?.Definition as string | null) ?? null,
        display: readStringField(inner, 'display'),
        system: readStringField(inner, 'system'),
        description: readStringField(inner, 'description'),
        shortName: readStringField(inner, 'shortName'),
      };
    });
  }, [habitQ.data]);

  const initialValues = useMemo<FormValues>(() => {
    if (!editing) {
      return {
        codeJson: '',
        historyCodeKey: '',
        fromDisplay: '',
        fromIso: null,
        toDisplay: '',
        toIso: null,
        comment: '',
      };
    }
    // Reconstruct the picker JSON from the read-side shape. On read the
    // category may not be present under code; preserve whatever was
    // there (Update will ship `code.category: null` anyway).
    const codeRow: SocialHistoryCodeRow | null = editing.code
      ? {
          code: editing.code.code ?? null,
          display: editing.code.display ?? null,
          system: editing.code.system ?? null,
          category: null,
        }
      : null;
    return {
      codeJson: codeRow ? JSON.stringify(codeRow) : '',
      historyCodeKey: editing.historyCode?.code ?? '',
      fromDisplay: editing.fromDisplayTime ?? '',
      fromIso: editing.fromTime ?? null,
      toDisplay: editing.toDisplayTime ?? '',
      toIso: editing.toTime ?? null,
      comment: editing.comment ?? '',
    };
  }, [editing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const createMut = useCreateSocialHistory();
  const updateMut = useUpdateSocialHistory();
  const isPending = createMut.isPending || updateMut.isPending;

  const submit = form.handleSubmit(async (values) => {
    if (!user) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }
    if (!userId) {
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }
    const codeRow = safeParse<SocialHistoryCodeRow>(values.codeJson);
    if (!codeRow) {
      toast.error('Form values are corrupted — please re-pick the type.');
      return;
    }
    const habitRow = habitRows.find(
      (r) => r.code === values.historyCodeKey,
    );
    if (!habitRow) {
      toast.error('Form values are corrupted — please re-pick the quantity.');
      return;
    }

    try {
      if (isEditing && editing) {
        // Strict audit guard — Social History's `createdBy` is the
        // ORIGINAL recording user (audit trail). Refusing to ship
        // rather than silently substituting the editing user
        // preserves the chart history's recorder attribution.
        //
        // **UX-visible failure mode** (Samer PR #245 review concern §5):
        // when this branch fires we surface a `toast.error(...)` via
        // sonner (project's global server-error transport — see
        // `src/shared/api/api-client.ts`) and `return` without
        // calling the mutation. The editor stays open with the
        // user's input intact so they can either close out or try a
        // different action. The user sees the toast banner — NOT a
        // silent no-op and NOT a console-only warning. The Storybook
        // story `EditModeMissingRecorder` demonstrates this path.
        if (!editing.createdBy) {
          toast.error(
            'Cannot edit — this record is missing its original recorder. Please contact support.',
          );
          return;
        }
        // On Update the wire ships `code.category: null` (backend
        // strips the nesting after Create — top-level `category`
        // holds the Coding instead). Round-trip the top-level
        // category from the original record (or from the picker if
        // the user changed the type — picker row carries it nested).
        const topLevelCategory: CodeWordCase | null =
          editing.category ?? codeRow.category ?? null;
        const body: UpdateSocialHistoryBody = {
          identifier: editing.identifier!,
          code: {
            code: codeRow.code ?? null,
            display: codeRow.display ?? null,
            system: codeRow.system ?? null,
            category: null,
          },
          status: statusForEdit(editing.status),
          category: topLevelCategory,
          comment: values.comment?.trim() ?? '',
          subject: patientId,
          encounter: editing.encounter ?? null,
          effectiveDateTime: null,
          fromDisplayTime: values.fromDisplay,
          fromTime: values.fromIso,
          toDisplayTime: values.toDisplay?.trim() ? values.toDisplay : null,
          toTime: values.toIso ?? null,
          historyCode: habitRow,
          dataAbsentReason: 'n/a',
          isDeleted: false,
          createdBy: editing.createdBy,
          createdDate: editing.createdDate ?? null,
          updatedby: null,
          updatedDate: null,
          performerDetails: normalizePerformerDetails(editing.performerDetails),
          speciality: editing.speciality ?? null,
        };
        await updateMut.mutateAsync({
          patientId,
          socialHistoryId: editing.identifier!,
          body,
        });
        toast.success(
          `Updated social history: ${codeRow.display ?? codeRow.code ?? 'Unknown'}`,
        );
      } else {
        // On Create the picker row's nested category is preserved
        // INSIDE `code`, and a duplicate top-level `category` is set
        // from the same source — matches the curl 2026-05-20.
        const nestedCategory = codeRow.category ?? null;
        const body: CreateSocialHistoryBody = {
          identifier: null,
          code: {
            code: codeRow.code ?? null,
            display: codeRow.display ?? null,
            system: codeRow.system ?? null,
            category: nestedCategory,
          },
          status: 'Active',
          category: nestedCategory,
          comment: values.comment?.trim() || null,
          subject: patientId,
          encounter: null,
          effectiveDateTime: null,
          fromDisplayTime: values.fromDisplay,
          fromTime: values.fromIso,
          toDisplayTime: values.toDisplay?.trim() ? values.toDisplay : null,
          toTime: values.toIso ?? null,
          historyCode: habitRow,
          dataAbsentReason: 'n/a',
          isDeleted: false,
          createdBy: userId,
          createdDate: null,
          updatedby: null,
          updatedDate: null,
          performerDetails: null,
          speciality: null,
        };
        await createMut.mutateAsync({ patientId, body });
        toast.success(
          `Added social history: ${codeRow.display ?? codeRow.code ?? 'Unknown'}`,
        );
      }
      form.reset();
      onClose();
    } catch {
      /* Global toast surfaces failure; keep editor open. */
    }
  });

  return (
    // The design's PaEditorCard: the one card (hairline, 12 corners, white, no shadow, 24 padding); look in
    // showcase-patient.css `editor card`.
    <div data-slot="editor-card">
      <form onSubmit={submit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            name="codeJson"
            control={form.control}
            render={({ field, fieldState }) => (
              <SocialHistoryTypePicker
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <div>
            <FieldLabel
              htmlFor="historyCode"
              
            >
              Quantity <span className="text-red-700">*</span>
            </FieldLabel>
            <Controller
              name="historyCodeKey"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <Select
                    id="historyCode"
                    className="mt-1"
                    value={field.value || undefined}
                    onChange={field.onChange}
                    disabled={habitQ.isLoading}
                    placeholder={habitQ.isLoading ? 'Loading…' : 'Pick a quantity'}
                    options={habitRows.map((row) => ({
                      label: row.display ?? row.code ?? '—',
                      value: row.code ?? '',
                    }))}
                  />
                  {fieldState.error ? (
                    <p
                      className="text-xs text-red-700 mt-1"
                      role="alert"
                    >
                      {fieldState.error.message}
                    </p>
                  ) : null}
                </>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            name="fromIso"
            control={form.control}
            render={({ field: isoField }) => (
              <Controller
                name="fromDisplay"
                control={form.control}
                render={({ field: displayField, fieldState }) => (
                  <DynamicDateInput
                    label="From"
                    required
                    displayValue={displayField.value}
                    isoValue={isoField.value}
                    onChange={(next) => {
                      displayField.onChange(next.displayValue);
                      isoField.onChange(next.isoValue);
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
          <Controller
            name="toIso"
            control={form.control}
            render={({ field: isoField }) => (
              <Controller
                name="toDisplay"
                control={form.control}
                render={({ field: displayField, fieldState }) => (
                  <DynamicDateInput
                    label="To"
                    displayValue={displayField.value ?? ''}
                    isoValue={isoField.value ?? null}
                    onChange={(next) => {
                      displayField.onChange(next.displayValue);
                      isoField.onChange(next.isoValue);
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
        </div>

        <div>
          <FieldLabel
            htmlFor="comment"
            
          >
            Comment
          </FieldLabel>
          <Textarea
            id="comment"
            {...form.register('comment')}
            placeholder="Optional context, e.g. quit attempts, intent to quit…"
            rows={3}
            maxLength={100}
            className="mt-1"
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <>
                Saving…
              </>
            ) : isEditing ? (
              'Update'
            ) : (
              'Done'
            )}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Type picker — Social-history-code typeahead
// ──────────────────────────────────────────────────────────────────────────

function SocialHistoryTypePicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const searchQ = useSocialHistoryCodeSearch(debounced, true);

  const selected = useMemo<SocialHistoryCodeRow | null>(
    () => (value ? safeParse<SocialHistoryCodeRow>(value) : null),
    [value],
  );

  const results = searchQ.data ?? [];

  return (
    <div>
      <FieldLabel
        htmlFor="social-type"
        
      >
        Type <span className="text-red-700">*</span>
      </FieldLabel>

      {selected ? (
        (() => {
          // Defensive re-unwrap — same pattern as other chart pickers.
          const inner = unwrapMasterRow(selected);
          const codeStr = readStringField(inner, 'code') ?? '';
          const displayStr = (readStringField(inner, 'display') ?? '').trim();
          const categoryDisplay = selected.category?.display ?? null;
          return (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border-default bg-blue-100/30 px-3 py-2">
              <div className="min-w-0">
                <p className={v2Look('text-sm font-medium text-brand-depth truncate', V2_BODY_EMPHASIS)}>
                  {displayStr || codeStr || '—'}
                </p>
                {categoryDisplay ? (
                  <p className="text-xs text-text-hint">{categoryDisplay}</p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => {
                  onChange('');
                  setQuery('');
                }}
              >
                Change
              </Button>
            </div>
          );
        })()
      ) : (
        <>
          <div className="relative mt-1">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              id="social-type"
              type="text"
              placeholder="Type to search (e.g. smoking, alcohol)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10"
              autoComplete="off"
            />
          </div>

          {debounced.trim().length >= 1 ? (
            <div className="mt-1 max-h-56 overflow-y-auto scrollbar-hide rounded-md border border-border-default bg-surface-card divide-y divide-border-default">
              {searchQ.isFetching ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  No matches. Try another word or a code.
                </div>
              ) : (
                results.map((row, idx) => {
                  // Defensive unwrap — same pattern as other chart
                  // pickers. See `unwrap-master-row.ts` for rationale.
                  const inner = unwrapMasterRow(row);
                  if (!inner) return null;
                  const codeStr = readStringField(inner, 'code') ?? '';
                  const displayStr = (readStringField(inner, 'display') ?? '').trim();
                  // Preserve the nested `category` Coding from the
                  // OUTER row (the wire body's `code.category` field).
                  const flat: SocialHistoryCodeRow = {
                    code: codeStr || null,
                    display: displayStr || null,
                    system: readStringField(inner, 'system'),
                    category: row.category ?? null,
                  };
                  const rowKey = `${codeStr}-${displayStr}-${idx}`;
                  return (
                    <button
                      key={rowKey}
                      type="button"
                      onClick={() => {
                        onChange(JSON.stringify(flat));
                        setQuery('');
                      }}
                      className="block w-full px-3 py-2 text-left hover:bg-grey-100/50"
                    >
                      <span className="text-sm text-text-primary block">
                        {displayStr || codeStr || '—'}
                      </span>
                      {flat.category?.display ? (
                        <span className="text-xs text-text-hint">
                          {flat.category.display}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function statusForEdit(
  status: string | null | undefined,
): 'Active' | 'Inactive' {
  return status === 'Inactive' ? 'Inactive' : 'Active';
}

// Unused-export — required to satisfy a future type-only import.
export type { SocialHabitQty };
